import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, LabelList } from 'recharts';

/**
 * Custom tooltip component for the demographic breakdown chart
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Get original data from the first payload entry
    const data = payload[0]?.payload;
    const lowSample = (data?.total || 0) < 3;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 font-rubik">
        <p className="font-semibold text-gray-800 mb-2">{label}</p>
        <p className="text-sm" style={{ color: '#08B23C' }}>
          Yes: {data?.yesCount} ({data?.yes}%)
        </p>
        <p className="text-sm" style={{ color: '#D33D3D' }}>
          No: {data?.noCount} ({data?.no}%)
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Total: {data?.total} vote{data?.total === 1 ? '' : 's'} in this group
        </p>
        {lowSample && (
          <p className="text-[10px] text-amber-700 mt-1 italic">
            Small sample — percentages may not reflect the broader population.
          </p>
        )}
      </div>
    );
  }
  return null;
};

/**
 * Custom label renderer for inside bars — shows the percentage only.
 */
const buildLabelRenderer = () => (props) => {
  const { x, y, width, height, value } = props;
  if (width < 30 || value < 5) return null;

  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill="white"
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={11}
      fontFamily="Rubik"
      fontWeight="500"
    >
      {value}%
    </text>
  );
};

/**
 * Stacked horizontal bar chart showing Yes/No breakdown by demographic
 * Green (Yes) on left, Red (No) on right
 */
function DemographicBreakdownChart({ data, demographic }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-gray-600 font-rubik">
          No responses yet for this demographic breakdown
        </p>
      </div>
    );
  }

  // Transform data for stacked bar chart
  const chartData = data.map(item => ({
    name: item.name,
    yes: item.yesPercentage,
    no: item.noPercentage,
    yesCount: item.yes,
    noCount: item.no,
    total: item.total
  }));

  // Build a name→total map so the y-axis tick can mark low-sample groups
  const totalByName = {};
  chartData.forEach(d => { totalByName[d.name] = d.total; });
  const hasLowSample = chartData.some(d => d.total > 0 && d.total < 3);

  // Calculate dynamic height based on number of items
  const barHeight = 40;
  const chartHeight = Math.max(200, chartData.length * barHeight + 60);

  // Y-axis tick: append "*" to groups with fewer than 3 votes so the small-sample
  // caveat is visible at a glance.
  const formatYTick = (name) => {
    const t = totalByName[name];
    return (t > 0 && t < 3) ? `${name} *` : name;
  };

  return (
    <div className="w-full" data-testid={`demographic-breakdown-${demographic}`}>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
          barSize={28}
        >
          <XAxis 
            type="number" 
            domain={[0, 100]}
            tick={{ fontFamily: 'Rubik', fontSize: 11 }}
            tickFormatter={(value) => `${value}%`}
          />
          <YAxis 
            dataKey="name" 
            type="category" 
            tick={{ fontFamily: 'Rubik', fontSize: 12 }}
            tickFormatter={formatYTick}
            width={120}
            tickLine={false}
          />
          <RechartsTooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="yes" 
            stackId="a" 
            fill="#08B23C"
            radius={[4, 0, 0, 4]}
          >
            <LabelList 
              dataKey="yes" 
              content={buildLabelRenderer()}
            />
          </Bar>
          <Bar 
            dataKey="no" 
            stackId="a" 
            fill="#D33D3D"
            radius={[0, 4, 4, 0]}
          >
            <LabelList 
              dataKey="no" 
              content={buildLabelRenderer()}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-2">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-primary"></div>
          <span className="text-sm font-medium text-gray-700 font-rubik">Yes</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-danger"></div>
          <span className="text-sm font-medium text-gray-700 font-rubik">No</span>
        </div>
      </div>

      {/* Caveat / explanation */}
      <p className="text-[11px] text-gray-500 text-center mt-2 font-rubik italic" data-testid={`demographic-breakdown-caption-${demographic}`}>
        Each bar shows how voters <em>within that group</em> answered — bars in different groups don&rsquo;t add up to 100%.
        {hasLowSample && ' Groups marked with * have fewer than 3 votes; treat their percentages as preliminary.'}
      </p>
    </div>
  );
}

export default DemographicBreakdownChart;
